import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import {
  Button, Segment, Modal, Image, Icon, Popup,
} from 'semantic-ui-react';
import {
  Other,
} from 'simplydo/interfaces';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import api from 'api';
import { ThemedButton } from 'components/lib/UI';

import utils from 'utils/utils';
import BusinessProfileEditor from './BusinessProfileEditor';
import FullBusinessProfile from './FullBusinessProfile';
import BusinessProfileSelector from './ProfileSelector';

const IdeaSection = styled.div`
  > h5 {
    display: block;
    margin: 0;
  }
  > .title-explainer {
    color: gray;
    display: block;
    margin: 2.5px 0 20px;
  }
`;

const DefaultCompanyDetails: Other.ICompanyDetails = {
  name: '',
  address: {
    streetAddress: '',
    country: '',
    city: '',
  },
  phoneNumber: '',
  websiteUrl: '',
  imageUrl: '',
  crunchbaseId: '',
  primaryContact: {
    fullName: '',
  },
};

type IIdeaBusinessProfileComponent = {
  idea: Other.IIdea,
  isEditing?: boolean,
  style?: object,
  updateIdea: (updatedValue: any, path: string[], suppressSave?: boolean) => void,
}

const IdeaBusinessProfileComponent = ({
  idea, isEditing, style, updateIdea,
}: IIdeaBusinessProfileComponent) => {
  // Track state of the profile editor - what stage the user is at
  const [verifyingCreditsafeEnabled, setVerifyingCreditsafeEnabled] = useState(false);
  const [creditsafeEnabled, setCreditsafeEnabled] = useState(false);
  const [showCreditsafeLookup, setShowCreditsafeLookup] = useState(false);
  const [selectedCreditsafeCompanyDetails, setSelectedCreditsafeCompanyDetails] = useState<Partial<Other.ICompanyDetails>|null>(null);
  const [companyDetails, setCompanyDetails] = useState<Other.ICompanyDetails>(DefaultCompanyDetails);

  const onUseSelectedCreditsafeCompanyDetails = useCallback(() => {
    setCompanyDetails((prevState) => ({
      ...prevState,
      ...selectedCreditsafeCompanyDetails,
    }));
    setSelectedCreditsafeCompanyDetails(null);
    setShowCreditsafeLookup(false);
  }, [selectedCreditsafeCompanyDetails]);

  const [saving, setSaving] = useState<boolean>(false);

  // The user's existing (previously created) organisation profiles
  const [loadingExistingProfiles, setLoadingExistingProfiles] = useState<boolean>(false);
  const [existingProfiles, setExistingProfiles] = useState<Other.IIdeaBusinessProfile[]>([]);

  const [attachedBusinessProfile, setAttachedBusinessProfile] = useState<Other.IIdeaBusinessProfile | null>(null);
  const [_loadingAttachedBusinessProfile, setLoadingAttachedBusinessProfile] = useState<boolean>(false);
  const [attachingBusinessProfile, setAttachingBusinessProfile] = useState<boolean>(false);
  const [unattachingBusinessProfile, setUnattachingBusinessProfile] = useState<boolean>(false);
  const [generatingCreditReport, setGeneratingCreditReport] = useState<boolean>(false);

  const [businessProfileEditorOpen, setBusinessProfileEditorOpen] = useState<boolean>(false);
  const [editingBusinessProfile, setEditingBusinessProfile] = useState<string>('');

  // _id of profile being deleted, if applicable
  const [deletingBusinessProfile, setDeletingBusinessProfile] = useState<string>('');

  const ideaId = idea?._id;
  const challengeId = idea?.challenge;
  const attachedBusinessProfileId = idea?.ideaBusinessProfile;

  const creditsafeRegNo = companyDetails?.creditsafeRegNo;
  useEffect(() => {
    setVerifyingCreditsafeEnabled(true);
    api.challenges.get(challengeId, (challenge) => {
      setCreditsafeEnabled(challenge.creditsafeAvailable);
      if (!creditsafeRegNo) {
        setShowCreditsafeLookup(challenge.creditsafeAvailable);
      } else {
        setShowCreditsafeLookup(false);
      }
      setVerifyingCreditsafeEnabled(false);
    }, () => {
      setShowCreditsafeLookup(false);
      setVerifyingCreditsafeEnabled(false);
    })
  }, [challengeId, creditsafeRegNo]);

  // Retrieve the attached organisation profile to an idea, if relevant
  // We retrieve it manually rather than attach in the API to loosely couple the components
  const getAttachedBusinessProfile = useCallback(() => {
    if (attachedBusinessProfileId) {
      setLoadingAttachedBusinessProfile(true);
      api.users.getProfile(attachedBusinessProfileId, ({ ideaBusinessProfile: receivedIdeaBusinessProfile }) => {
        setAttachedBusinessProfile(receivedIdeaBusinessProfile);
        setLoadingAttachedBusinessProfile(false);
      }, () => {
        setLoadingAttachedBusinessProfile(false);
      });
    }
  }, [attachedBusinessProfileId]);

  useEffect(() => {
    getAttachedBusinessProfile();
  }, [getAttachedBusinessProfile]);

  // Attach a new, or previously created organisation profile
  // Once done, update the idea locally and display the preview of the organisation profile
  const attachBusinessProfile = useCallback((businessProfileId) => {
    setAttachingBusinessProfile(true);
    api.ideas.attachBusinessProfile(ideaId, businessProfileId, () => {
      updateIdea(businessProfileId, ['ideaBusinessProfile']);
      setAttachingBusinessProfile(false);
    }, (err) => {
      toast.error(err.message);
      setAttachingBusinessProfile(false);
    });
  }, [updateIdea, ideaId]);

  const unattachBusinessProfile = useCallback(() => {
    utils.confirm(
      `Remove ${attachedBusinessProfile?.profile.name} from this idea`,
      `Are you sure you want to remove the organisation profile ${attachedBusinessProfile?.profile.name} from this idea?`,
    )
      .then(() => {
        setUnattachingBusinessProfile(true);
        api.ideas.unattachBusinessProfile(ideaId, () => {
          updateIdea(null, ['ideaBusinessProfile']);
          setUnattachingBusinessProfile(false);
          setAttachedBusinessProfile(null);
        }, (err) => {
          toast.error(err.message);
          setUnattachingBusinessProfile(false);
        });
      });
  }, [updateIdea, ideaId, attachedBusinessProfile]);

  const viewReportForCompany = useCallback(() => {
    if (attachedBusinessProfile) {
      setGeneratingCreditReport(true);
      api.users.generateIdeaBusinessProfileCreditReport(attachedBusinessProfile._id, ({ success, creditsafeReportUrl }) => {
        if (!success) {
          toast.error('Could not generate CreditSafe report');
          setGeneratingCreditReport(false);
          return;
        }
        setAttachedBusinessProfile((prevState) => {
          if (!prevState) return prevState;
          return {
            ...prevState,
            creditsafeReportUrl,
          };
        });
        setGeneratingCreditReport(false);
        window.open(creditsafeReportUrl, '_blank', 'noopener noreferrer');
      }, (err) => {
        toast.error(err.message);
        setGeneratingCreditReport(false);
      });
    }
  }, [attachedBusinessProfile]);

  // Allow user to retrieve from their previously created profiles
  const getExistingBusinessProfiles = useCallback(() => {
    setLoadingExistingProfiles(true);
    api.users.getExistingProfiles((({ profiles }) => {
      setLoadingExistingProfiles(false);
      setExistingProfiles(profiles);
    }), (err) => {
      setLoadingExistingProfiles(false);
      toast.error(err.message);
    });
  }, []);

  useEffect(() => {
    getExistingBusinessProfiles();
  }, [getExistingBusinessProfiles]);

  // Create new profile, if needed
  // If we are editing, save the edit instead
  const saveProfile = useCallback(() => {
    if (!editingBusinessProfile) {
      setSaving(true);
      api.users.createProfile(companyDetails, ({ ideaBusinessProfile: newIdeaBusinessProfile }) => {
        toast.success('Profile updated');
        setExistingProfiles((prevProfiles) => ([...prevProfiles, newIdeaBusinessProfile]));
        setSaving(false);
        setBusinessProfileEditorOpen(false);
        attachBusinessProfile(newIdeaBusinessProfile._id);
        updateIdea(newIdeaBusinessProfile._id, ['ideaBusinessProfile']);
      }, (err) => {
        toast.error(err.message);
        setSaving(false);
      });
    } else {
      setSaving(true);
      api.users.updateProfile(editingBusinessProfile, companyDetails, ({ ideaBusinessProfile: updatedIdeaBusinessProfile }) => {
        toast.success('Profile updated');
        setExistingProfiles((prevProfiles) => [...prevProfiles.map((prevProfile) => {
          if (prevProfile._id !== editingBusinessProfile) return prevProfile;
          return updatedIdeaBusinessProfile;
        })]);
        setEditingBusinessProfile('');
        if (attachedBusinessProfile?._id === editingBusinessProfile) {
          setAttachedBusinessProfile(updatedIdeaBusinessProfile);
        }
        setSaving(false);
        setBusinessProfileEditorOpen(false);
        updateIdea(editingBusinessProfile, ['ideaBusinessProfile']);
      }, (err) => {
        toast.error(err.message);
        setSaving(false);
      });
    }
  }, [companyDetails, editingBusinessProfile, attachedBusinessProfile, attachBusinessProfile, updateIdea]);

  const deleteProfile = useCallback((businessProfileId) => {
    const existingBusinessProfile = existingProfiles.find((p) => p._id === businessProfileId);
    if (!existingBusinessProfile) return;

    utils.confirm(
      `Really delete the organisation profile ${existingBusinessProfile.profile.name}?`,
      `Deleting the organisation profile ${existingBusinessProfile.profile.name} is permanent and cannot be undone.`,
    )
      .then(() => {
        setDeletingBusinessProfile(businessProfileId);
        // Firstly, unattach the profile from the current idea
        // Secondly, attempt to delete the profile.
        // If the profile is attached to another idea, we alert the user, and don't continue with this action
        api.ideas.unattachBusinessProfile(ideaId, () => {
          api.users.deleteProfile(businessProfileId, ({ success: deletingSuccess, reason }: { success: boolean, reason: string }) => {
            if (!deletingSuccess) {
              toast(reason);
              setDeletingBusinessProfile('');
            } else {
              // Have to take a number of actions if deleting and unattaching was successful
              // Update the local states, so the organisation profile has been cleared from:
              // - The local idea state
              // - The local existing profiles
              // - The active company details
              // Then also close the editor
              updateIdea(null, ['ideaBusinessProfile'], true);
              setAttachedBusinessProfile(null);
              setExistingProfiles((prevProfiles) => prevProfiles.filter((p) => p._id !== businessProfileId));
              setEditingBusinessProfile('');
              setCompanyDetails(DefaultCompanyDetails);
              setBusinessProfileEditorOpen(false);
            }
          }, (err) => {
            toast.error(err.message);
            setDeletingBusinessProfile('');
          });
        }, (err) => {
          toast.error(err.message);
          setDeletingBusinessProfile('');
        });
      });
  }, [ideaId, existingProfiles, updateIdea]);

  // Given a organisation profile id to edit, set the details
  const setEditingProfile = useCallback((profileId: string) => {
    const profileObject = existingProfiles.find((p) => p._id === profileId);
    if (profileObject) {
      setCompanyDetails(profileObject.profile);
      setEditingBusinessProfile(profileId);
      setBusinessProfileEditorOpen(true);
    }
  }, [existingProfiles]);

  const createNewCompany = useCallback(() => {
    setEditingBusinessProfile('');
    setCompanyDetails(DefaultCompanyDetails);
    setBusinessProfileEditorOpen(true);
  }, []);

  const editingFoundCompany = useMemo(() => (companyDetails.crunchbaseId ? companyDetails : existingProfiles.find((existingProfile) => existingProfile._id === editingBusinessProfile)?.profile), [companyDetails, existingProfiles, editingBusinessProfile]);

  const editorProps = {
    setCompanyDetails,
  };

  if (!isEditing && !attachedBusinessProfile) {
    return null;
  }

  return (
    <IdeaSection style={style}>
      <h3>
        Organisation details{isEditing && idea.ideaTemplate?.requireIdeaBusinessProfiles ? (
          <Popup size='small' trigger={<Icon name='asterisk' style={{ position: 'relative', top: -5, marginLeft: 3 }} color='red' size='tiny' />} content='This field must be filled in to submit your idea' />) : null
        }</h3>
      {isEditing
        ? (
          <span className='title-explainer'>Please provide your organisation's details to supplement your idea.</span>
        ) : (
          <div style={{ marginBottom: 5 }} />
        )
      }

      {!isEditing
        ? (
          <FullBusinessProfile
            viewReportForCompany={viewReportForCompany}
            generatingCreditReport={generatingCreditReport}
            ideaBusinessProfile={attachedBusinessProfile}
            idea={idea}
          />
        ) : ([
          attachedBusinessProfile
            ? (
              <Segment>
                <FullBusinessProfile
                  idea={idea}
                  ideaBusinessProfile={attachedBusinessProfile}
                  viewReportForCompany={viewReportForCompany}
                  generatingCreditReport={generatingCreditReport}
                />
              </Segment>
            ) : null,
          <BusinessProfileSelector
            attachedBusinessProfile={attachedBusinessProfile}
            attachingBusinessProfile={attachingBusinessProfile || loadingExistingProfiles}
            existingProfiles={existingProfiles}
            createNewCompany={createNewCompany}
            attachBusinessProfile={(businessProfileId) => attachBusinessProfile(businessProfileId)}
          />,
          attachedBusinessProfile
            ? (
              <div
                style={{
                  display: 'flex', width: '100%', justifyContent: 'flex-end', marginBottom: 5,
                }}
              >
                <Button
                  basic
                  content='Edit this organisation profile'
                  icon='edit'
                  onClick={() => setEditingProfile(attachedBusinessProfile._id)}
                  size='small'
                />
                <Button
                  basic
                  content={`Remove ${attachedBusinessProfile.profile.name} from this idea`}
                  icon='close'
                  onClick={() => unattachBusinessProfile()}
                  size='small'
                  loading={unattachingBusinessProfile}
                  style={{ marginRight: 0 }}
                />
              </div>
            ) : null,
        ].filter((segment) => !!segment))
      }

      <Modal
        open={businessProfileEditorOpen}
        onClose={() => setBusinessProfileEditorOpen(false)}
      >
        <Modal.Header>
          {editingFoundCompany
            ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {editingFoundCompany.imageUrl
                    ? (
                      <Image avatar style={{ objectFit: 'contain', width: 45, height: 30 }} src={editingFoundCompany.imageUrl} />
                    ) : (
                      <Icon name='building' size='large' />
                    )
                  }
                  <span style={{ display: 'block', margin: '0 0 0 5px' }}>
                    Editing organisation profile for {editingFoundCompany.name}
                  </span>
                </div>

                {editingBusinessProfile
                  ? <Button
                    icon='trash'
                    content='Delete profile'
                    floated='right'
                    basic
                    onClick={() => deleteProfile(editingBusinessProfile)}
                    loading={deletingBusinessProfile === editingBusinessProfile}
                  /> : null}
              </div>
            ) : (
              'Create new organisation profile'
            )
          }
        </Modal.Header>
        <Modal.Content scrolling style={{ height: '100%' }}>
          <BusinessProfileEditor
            {...editorProps}
            ideaId={ideaId}
            companyDetails={companyDetails}
            selectedCreditsafeCompanyDetails={selectedCreditsafeCompanyDetails}
            setSelectedCreditsafeCompanyDetails={setSelectedCreditsafeCompanyDetails}
            showCreditsafeLookup={showCreditsafeLookup}
            verifyingCreditsafeEnabled={verifyingCreditsafeEnabled}
          />
        </Modal.Content>
        <Modal.Actions>
          {showCreditsafeLookup
            ? (
              <>
                <Button
                  icon='arrow right'
                  labelPosition='right'
                  content='Enter organisation details manually'
                  onClick={() => setShowCreditsafeLookup(false)}
                />
                {selectedCreditsafeCompanyDetails
                  ? (
                    <Button
                      icon='check'
                      content='Use selected organisation details'
                      primary
                      labelPosition='right'
                      onClick={onUseSelectedCreditsafeCompanyDetails}
                    />
                  ) : null
                }
              </>
            ) : (
              <>
                {creditsafeEnabled
                  ? (
                    <Button
                      icon='arrow left'
                      labelPosition='left'
                      content='Use CreditSafe lookup'
                      onClick={() => setShowCreditsafeLookup(true)}
                    />
                  ) : null
                }
                <ThemedButton disabled={!companyDetails.name} primary onClick={() => saveProfile()} content='Save profile' icon='check' loading={saving} />
              </>
            )
          }
        </Modal.Actions>
      </Modal>
    </IdeaSection>
  );
};

export default IdeaBusinessProfileComponent;
